import { useCallback, useEffect, useState } from 'react';
import { Archive, CheckCircle2, Combine, Loader2, Plus, Undo2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import { claseDeColor, NOMBRE_COLOR } from './etiquetas';

/* =====================================================================
   ETIQUETAS · CATÁLOGO VIVO F4.1 y F4.5 (pestaña de Catálogo)
   =====================================================================
   Crear con color («¿es esta?» antes de duplicar), cambiar nombre y color,
   juntar dos que dicen lo mismo y archivar (con deshacer). Poner etiquetas
   a un producto sigue en su ficha de Productos.
   ===================================================================== */
const mensaje = (e) => (e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');
const BOTON = 'min-h-11 inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-3 text-sm';

export default function GestionEtiquetas() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const [nueva, setNueva] = useState({ nombre: '', color: 'azul' });
  const [parecidas, setParecidas] = useState(null);
  const [archivada, setArchivada] = useState(null);
  const [juntar, setJuntar] = useState(null);       // { origen, destino }
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(() => {
    api.get('/catalogo-vivo/etiquetas')
      .then((r) => { setError(null); setDatos(r); })
      .catch((e) => setError(mensaje(e)));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  async function crear(aunque = false) {
    setEnviando(true);
    setError(null);
    setHecho(null);
    try {
      const r = await api.post('/catalogo-vivo/etiquetas', {
        nombre: nueva.nombre.trim(), color: nueva.color, crear_aunque_se_parezca: aunque });
      if (!r.creada) { setParecidas(r); return; }
      setParecidas(null);
      setNueva({ nombre: '', color: nueva.color });
      setHecho(`Listo: la etiqueta «${r.etiqueta.nombre}» ya se puede poner en los productos.`);
      cargar();
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setEnviando(false);
    }
  }

  async function cambiar(e, cambios, aviso) {
    setError(null);
    try {
      await api.patch(`/catalogo-vivo/etiquetas/${e.id}`, cambios);
      setHecho(aviso);
      cargar();
    } catch (err) {
      setError(mensaje(err));
    }
  }

  async function archivar(e) {
    setError(null);
    try {
      await api.post(`/catalogo-vivo/etiquetas/${e.id}/archivar`);
      setArchivada(e);
      setHecho(`Listo: «${e.nombre}» ya no se ofrece. Los productos que la llevaban dejan de mostrarla.`);
      cargar();
    } catch (err) {
      setError(mensaje(err));
    }
  }

  async function deshacer() {
    try {
      await api.post(`/catalogo-vivo/etiquetas/${archivada.id}/reactivar`);
      setHecho(`Listo: «${archivada.nombre}» volvió.`);
      setArchivada(null);
      cargar();
    } catch (err) {
      setError(mensaje(err));
    }
  }

  async function confirmarJuntar() {
    try {
      const r = await api.post(`/catalogo-vivo/etiquetas/${juntar.origen.id}/fusionar`, { id_destino: juntar.destino.id });
      setHecho(`Listo: «${r.origen}» quedó dentro de «${r.destino}».`);
      setJuntar(null);
      cargar();
    } catch (err) {
      setError(mensaje(err));
      setJuntar(null);
    }
  }

  if (error && !datos) return <p role="alert" className="text-sm text-red-700">{error}</p>;
  if (!datos) return <p className="text-sm text-gray-600 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo las etiquetas…</p>;
  const puede = datos.puede_gestionar;
  const sinPermiso = 'Ordenar las etiquetas lo hace el dueño o quien tenga el permiso «Ordenar las etiquetas».';
  const motivoCrear = !puede ? sinPermiso : !nueva.nombre.trim() ? 'Escribe el nombre de la etiqueta.' : null;

  return (
    <div className="space-y-3" data-catalogo-vivo="etiquetas">
      <Ayuda id="catalogo_vivo.etiquetas.pantalla" />
      {hecho && <p role="status" className="text-sm text-green-800 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />{hecho}</p>}
      {archivada && (
        <button type="button" onClick={deshacer} className={`${BOTON} bg-white border border-gray-300 text-gray-800`}>
          <Undo2 className="h-4 w-4" aria-hidden="true" /> Deshacer: volver a ofrecer «{archivada.nombre}»
        </button>
      )}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <form onSubmit={(e) => { e.preventDefault(); if (!motivoCrear) crear(false); }}
        className="rounded-xl border border-gray-200 p-3 space-y-2" data-catalogo-vivo="nueva-etiqueta">
        <label htmlFor="cv-nueva-etiqueta" className="block text-sm font-semibold text-gray-800">Nueva etiqueta</label>
        <input id="cv-nueva-etiqueta" value={nueva.nombre} maxLength={40} autoComplete="off"
          onChange={(e) => { setParecidas(null); setNueva((n) => ({ ...n, nombre: e.target.value })); }}
          placeholder="Por ejemplo: proyecto-helio" className="w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base" />
        <label className="block text-sm text-gray-700">Color
          <select value={nueva.color} onChange={(e) => setNueva((n) => ({ ...n, color: e.target.value }))}
            className="mt-1 w-full min-h-11 rounded-xl border border-gray-300 px-3 text-base bg-white">
            {datos.colores.map((c) => <option key={c} value={c}>{NOMBRE_COLOR[c] || c}</option>)}
          </select>
        </label>
        <Ayuda id="catalogo_vivo.etiquetas.nueva" />
        <button type="submit" disabled={Boolean(motivoCrear) || enviando}
          className={`${BOTON} w-full bg-[var(--color-marca)] text-white disabled:opacity-60`}>
          {enviando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />} Crear etiqueta
        </button>
        {motivoCrear && (nueva.nombre || !puede) && <p className="text-xs text-amber-800" data-motivo="nueva-etiqueta">{motivoCrear}</p>}
        {parecidas && (
          <div className="rounded-lg bg-amber-50 p-2 space-y-2" data-catalogo-vivo="es-esta">
            <p className="text-sm text-amber-900">
              {parecidas.igual ? 'Ya existe una etiqueta con ese nombre:' : '¿Es alguna de éstas?'}{' '}
              {parecidas.parecidas.map((p) => `«${p.nombre}»`).join(', ')}.
            </p>
            {!parecidas.igual && (
              <button type="button" onClick={() => crear(true)} className={`${BOTON} bg-white border border-gray-300 text-gray-800`}>
                No, crear «{nueva.nombre.trim()}»
              </button>
            )}
          </div>
        )}
      </form>

      {datos.etiquetas.length === 0 ? (
        <p className="text-sm text-gray-600 rounded-xl border border-gray-200 p-3" data-catalogo-vivo="sin-etiquetas">
          No hay etiquetas. Sirven para ver y filtrar productos de varias categorías juntos, como «proyecto-helio» o «urgente». Crea la primera arriba.
        </p>
      ) : (
        <ul className="space-y-2">
          {datos.etiquetas.map((e) => (
            <li key={e.id} className="rounded-xl border border-gray-200 p-3 space-y-2" data-etiqueta={e.id}>
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-3 py-1 text-sm ${claseDeColor(e.color)}`}>{e.nombre}</span>
                <span className="text-xs text-gray-600">{e.productos} producto(s)</span>
              </div>
              {puede && (
                <div className="flex flex-wrap gap-2">
                  <label className="text-sm text-gray-700">Color{' '}
                    <select value={e.color} onChange={(ev) => cambiar(e, { color: ev.target.value }, `Listo: «${e.nombre}» ahora es ${NOMBRE_COLOR[ev.target.value]?.toLowerCase()}.`)}
                      className="min-h-11 rounded-xl border border-gray-300 px-2 bg-white">
                      {datos.colores.map((c) => <option key={c} value={c}>{NOMBRE_COLOR[c] || c}</option>)}
                    </select>
                  </label>
                  <label className="text-sm text-gray-700">Juntar con{' '}
                    <select value="" onChange={(ev) => setJuntar({ origen: e, destino: datos.etiquetas.find((x) => String(x.id) === ev.target.value) })}
                      className="min-h-11 rounded-xl border border-gray-300 px-2 bg-white">
                      <option value="">Elige…</option>
                      {datos.etiquetas.filter((x) => x.id !== e.id).map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
                    </select>
                  </label>
                  <button type="button" onClick={() => archivar(e)} className={`${BOTON} bg-white border border-gray-300 text-gray-800`}>
                    <Archive className="h-4 w-4" aria-hidden="true" /> Archivar
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!puede && <p className="text-xs text-gray-600">{sinPermiso}</p>}
      <Ayuda id="catalogo_vivo.etiquetas.juntar" />
      {juntar?.destino && (
        <ModalConfirmar
          titulo={`Juntar «${juntar.origen.nombre}» en «${juntar.destino.nombre}»`}
          descripcion={`Los ${juntar.origen.productos} producto(s) con «${juntar.origen.nombre}» pasan a llevar «${juntar.destino.nombre}», y «${juntar.origen.nombre}» queda archivada. Esto no se deshace.`}
          etiquetaConfirmar="Sí, juntar"
          peligro
          onConfirmar={confirmarJuntar}
          onCerrar={() => setJuntar(null)}
        />
      )}
      <p className="text-xs text-gray-500"><Combine className="h-3.5 w-3.5 inline" aria-hidden="true" /> {datos.etiquetas.length} de {datos.tope} etiquetas posibles en tu empresa.</p>
    </div>
  );
}
